import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Grid,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  Divider,
  Chip,
  Switch,
  CircularProgress,
  Stack,
  Paper,
} from '@mui/material';
import {
  Save,
  Layers,
  Users,
  ShieldCheck,
  Building2,
  CheckSquare,
  Square,
  UsersRound,
  CreditCard,
  Wallet,
  GraduationCap,
  FileText,
  Calendar,
  Bus,
  Building,
  BookOpen,
  Package,
  Megaphone,
  DollarSign,
  BarChart,
  BadgeCheck,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useSuperAdminSchoolStore } from '@/store/superAdmin/schoolStore';
import { roleAccessService } from '@/services/superAdmin/roleAccessService';
import { PageHeader } from '@/components/navigation/PageHeader';
import { Loader } from '@/components/feedback/Loader';
import { ErrorState } from '@/components/feedback/ErrorState';
import { tokens } from '@/theme/tokens';

const getModuleIcon = (iconName) => {
  const iconMap = {
    people: Users,
    badge: BadgeCheck,
    payments: CreditCard,
    family_restroom: UsersRound,
    account_balance_wallet: Wallet,
    school: GraduationCap,
    fact_check: CheckSquare,
    assignment: FileText,
    calendar_month: Calendar,
    directions_bus: Bus,
    hotel: Building,
    menu_book: BookOpen,
    inventory: Package,
    campaign: Megaphone,
    money: DollarSign,
    analytics: BarChart,
  };
  return iconMap[iconName] || Layers;
};

export function RoleAccessPage() {
  const { globalSchools, fetchAllSchools } = useSuperAdminSchoolStore();

  const [selectedSchoolId, setSelectedSchoolId] = useState('');
  const [roles, setRoles] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [rolesLoading, setRolesLoading] = useState(false);

  const [roleAccessData, setRoleAccessData] = useState(null);
  const [accessLoading, setAccessLoading] = useState(false);
  const [error, setError] = useState(null);

  // Map of enabled module_id -> array of enabled menu_ids
  const [selectedMenuMap, setSelectedMenuMap] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchAllSchools();
  }, [fetchAllSchools]);

  useEffect(() => {
    if (!selectedSchoolId) {
      setRoles([]);
      setSelectedRoleId('');
      setRoleAccessData(null);
      setSelectedMenuMap({});
      return;
    }

    const fetchRoles = async () => {
      setRolesLoading(true);
      setError(null);
      try {
        const rolesList = await roleAccessService.getSchoolRoles(selectedSchoolId);
        setRoles(rolesList);
        if (rolesList.length > 0) {
          setSelectedRoleId(rolesList[0].id);
        } else {
          setSelectedRoleId('');
          setRoleAccessData(null);
        }
      } catch (err) {
        setError(err.message || 'Failed to fetch roles for selected school');
        toast.error('Failed to load school roles');
      } finally {
        setRolesLoading(false);
      }
    };

    fetchRoles();
  }, [selectedSchoolId]);

  useEffect(() => {
    if (!selectedSchoolId || !selectedRoleId) {
      setRoleAccessData(null);
      setSelectedMenuMap({});
      return;
    }

    const fetchAccess = async () => {
      setAccessLoading(true);
      setError(null);
      try {
        const accessRes = await roleAccessService.getRoleAccess(selectedSchoolId, selectedRoleId);
        setRoleAccessData(accessRes);

        const initialMap = {};
        (accessRes?.modules || []).forEach((mod) => {
          const enabledMenuIds = (mod.menus || [])
            .filter((m) => m.enabled)
            .map((m) => m.id);

          if (mod.enabled) {
            initialMap[mod.id] = enabledMenuIds;
          }
        });
        setSelectedMenuMap(initialMap);
      } catch (err) {
        setError(err.message || 'Failed to fetch role module access');
        toast.error('Failed to load module access configuration');
      } finally {
        setAccessLoading(false);
      }
    };

    fetchAccess();
  }, [selectedSchoolId, selectedRoleId]);

  const handleToggleModule = (mod) => {
    setSelectedMenuMap((prev) => {
      const next = { ...prev };
      if (mod.id in next) {
        delete next[mod.id];
      } else {
        next[mod.id] = (mod.menus || []).map((m) => m.id);
      }
      return next;
    });
  };

  const handleToggleMenu = (mod, menuId) => {
    setSelectedMenuMap((prev) => {
      const next = { ...prev };
      const currentMenuIds = next[mod.id] ? [...next[mod.id]] : [];

      let updatedMenuIds;
      if (currentMenuIds.includes(menuId)) {
        updatedMenuIds = currentMenuIds.filter((id) => id !== menuId);
      } else {
        updatedMenuIds = [...currentMenuIds, menuId];
      }

      next[mod.id] = updatedMenuIds;
      return next;
    });
  };

  const handleSelectAll = () => {
    const fullMap = {};
    (roleAccessData?.modules || []).forEach((mod) => {
      fullMap[mod.id] = (mod.menus || []).map((m) => m.id);
    });
    setSelectedMenuMap(fullMap);
    toast.success('All modules and menus selected');
  };

  const handleDeselectAll = () => {
    setSelectedMenuMap({});
    toast.success('All modules and menus cleared');
  };

  const handleSave = async () => {
    if (!selectedSchoolId || !selectedRoleId) {
      toast.error('Please select both a school and a role');
      return;
    }

    setIsSaving(true);
    try {
      const modulesPayload = (roleAccessData?.modules || [])
        .map((mod) => ({
          module_id: mod.id,
          menu_ids: selectedMenuMap[mod.id] || [],
        }))
        .filter((mod) => Array.isArray(mod.menu_ids) && mod.menu_ids.length > 0);

      const res = await roleAccessService.updateRoleAccess(selectedSchoolId, selectedRoleId, modulesPayload);
      toast.success(res?.message || 'Role access assigned successfully.');
    } catch (err) {
      toast.error(err.message || 'Failed to update role access configuration');
    } finally {
      setIsSaving(false);
    }
  };

  const selectedRoleObj = roles.find((r) => r.id === selectedRoleId);

  return (
    <Box sx={{ pb: 4 }}>
      <PageHeader
        title="Role Access Management"
        subtitle="Configure role-based feature modules and navigation menu permissions for each school."
        action={
          <Button
            variant="contained"
            startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <Save size={18} />}
            onClick={handleSave}
            disabled={!selectedSchoolId || !selectedRoleId || isSaving || accessLoading}
            sx={{
              borderRadius: tokens.radius.sm,
              fontWeight: 600,
              px: 3,
              textTransform: 'none',
            }}
          >
            {isSaving ? 'Saving...' : 'Save Configuration'}
          </Button>
        }
      />

      {/* Control Panel */}
      <Card
        elevation={0}
        sx={{
          borderRadius: tokens.radius.md,
          border: `1px solid ${tokens.colors.border}`,
          mb: 3.5,
          bgcolor: 'background.paper',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={3} sx={{ alignItems: 'center' }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Building2 size={16} /> SELECT SCHOOL *
              </Typography>
              <Select
                fullWidth
                value={selectedSchoolId}
                onChange={(e) => setSelectedSchoolId(e.target.value)}
                displayEmpty
                sx={{ borderRadius: tokens.radius.sm }}
              >
                <MenuItem value="" disabled>
                  Choose a school to configure...
                </MenuItem>
                {globalSchools.map((school) => (
                  <MenuItem key={school.id} value={school.id}>
                    {school.school_name || school.name}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <ShieldCheck size={16} /> SELECT ROLE *
              </Typography>
              <Select
                fullWidth
                value={selectedRoleId}
                onChange={(e) => setSelectedRoleId(e.target.value)}
                displayEmpty
                disabled={!selectedSchoolId || rolesLoading}
                sx={{ borderRadius: tokens.radius.sm }}
              >
                <MenuItem value="" disabled>
                  {rolesLoading ? 'Loading roles...' : !selectedSchoolId ? 'Select a school first' : 'Choose a role...'}
                </MenuItem>
                {roles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    {role.description || role.name} ({role.name})
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            {selectedSchoolId && roles.length > 0 && (
              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', pt: 1 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mr: 1 }}>
                    Quick Role Select:
                  </Typography>
                  {roles.map((r) => {
                    const isSelected = r.id === selectedRoleId;
                    return (
                      <Chip
                        key={r.id}
                        label={r.description || r.name}
                        onClick={() => setSelectedRoleId(r.id)}
                        color={isSelected ? 'primary' : 'default'}
                        variant={isSelected ? 'filled' : 'outlined'}
                        size="small"
                        sx={{ fontWeight: 600, cursor: 'pointer' }}
                      />
                    );
                  })}
                </Box>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {error && <ErrorState message={error} onRetry={() => setSelectedSchoolId(selectedSchoolId)} />}

      {!selectedSchoolId && !error && (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderRadius: tokens.radius.md }}>
          <Building2 size={48} style={{ opacity: 0.3, marginBottom: 12 }} />
          <Typography variant="h6" fontWeight={600} color="text.secondary" gutterBottom>
            No School Selected
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please select a school from the dropdown above to view and assign role permissions.
          </Typography>
        </Paper>
      )}

      {selectedSchoolId && !selectedRoleId && !rolesLoading && (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderRadius: tokens.radius.md }}>
          <ShieldCheck size={48} style={{ opacity: 0.3, marginBottom: 12 }} />
          <Typography variant="h6" fontWeight={600} color="text.secondary" gutterBottom>
            No Role Selected
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please choose a role above to configure module and menu access.
          </Typography>
        </Paper>
      )}

      {accessLoading && <Loader message="Loading role module permissions..." />}

      {selectedSchoolId && selectedRoleId && !accessLoading && roleAccessData && (
        <Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
              mb: 3,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="h6" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Layers size={22} color={tokens.colors.primary.main} /> Module Permissions for
              </Typography>
              <Chip
                label={selectedRoleObj?.description || roleAccessData.role_name}
                color="primary"
                sx={{ fontWeight: 700, fontSize: 13 }}
              />
            </Box>

            <Stack direction="row" spacing={1.5}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<CheckSquare size={16} />}
                onClick={handleSelectAll}
              >
                Select All
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                startIcon={<Square size={16} />}
                onClick={handleDeselectAll}
              >
                Clear All
              </Button>
            </Stack>
          </Box>

          <Grid container spacing={3}>
            {(roleAccessData.modules || []).map((mod) => {
              const isModuleEnabled = mod.id in selectedMenuMap;
              const enabledMenus = selectedMenuMap[mod.id] || [];
              const IconComponent = getModuleIcon(mod.icon);

              return (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={mod.id}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: tokens.radius.md,
                      border: `1px solid ${isModuleEnabled ? tokens.colors.primary.main : tokens.colors.border}`,
                      backgroundColor: isModuleEnabled ? `${tokens.colors.primary.main}08` : 'background.paper',
                      transition: 'all 0.2s ease',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box
                            sx={{
                              width: 42,
                              height: 42,
                              borderRadius: tokens.radius.sm,
                              backgroundColor: isModuleEnabled ? tokens.colors.primary.main : tokens.colors.grey[100],
                              color: isModuleEnabled ? '#fff' : tokens.colors.text.secondary,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <IconComponent size={22} />
                          </Box>
                          <Box>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                              {mod.display_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                              {mod.name}
                            </Typography>
                          </Box>
                        </Box>

                        <Switch
                          checked={isModuleEnabled}
                          onChange={() => handleToggleModule(mod)}
                          color="primary"
                        />
                      </Box>

                      <Divider sx={{ my: 1.5 }} />

                      <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        NAVIGATION MENUS ({enabledMenus.length} / {mod.menus ? mod.menus.length : 0})
                      </Typography>

                      {(!mod.menus || mod.menus.length === 0) ? (
                        <Typography variant="body2" color="text.secondary" sx={{ italic: true, py: 1 }}>
                          No sub-menus configured for this module.
                        </Typography>
                      ) : (
                        <Stack spacing={0.5}>
                          {mod.menus.map((menu) => {
                            const isMenuChecked = enabledMenus.includes(menu.id);
                            return (
                              <FormControlLabel
                                key={menu.id}
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={isMenuChecked}
                                    onChange={() => handleToggleMenu(mod, menu.id)}
                                    color="primary"
                                  />
                                }
                                label={
                                  <Typography variant="body2" fontWeight={isMenuChecked ? 600 : 400}>
                                    {menu.display_name}
                                  </Typography>
                                }
                                sx={{ ml: -0.5, my: -0.2 }}
                              />
                            );
                          })}
                        </Stack>
                      )}
                    </CardContent>

                    <Box sx={{ px: 3, pb: 2, pt: 0 }}>
                      <Chip
                        label={isModuleEnabled ? 'ENABLED FOR ROLE' : 'DISABLED'}
                        size="small"
                        color={isModuleEnabled ? 'success' : 'default'}
                        variant={isModuleEnabled ? 'filled' : 'outlined'}
                        sx={{ fontSize: 10, fontWeight: 700, borderRadius: tokens.radius.xs }}
                      />
                    </Box>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}
    </Box>
  );
}
